import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  Kitchen,
  ListProductivityMetricsQuery,
  ListProductivityMetricsQueryVariables,
  ProductivityMetrics,
  ProductivityMetricsFood
} from '../libs';
import { promisePoolForTasks, splitDateRangeByDays } from './utils';
import { GraphQLClient, LIST_PRODUCTIVITY_METRICS_QUERY } from '../libs/graphQL';

interface Props {
  startDate: string;
  endDate: string;
  kitchen: Kitchen;
}

const groupProductivityMetricsByDayAndShift = (flatData: ProductivityMetricsFood[]): ProductivityMetrics[] => {
  const grouped = new Map<string, ProductivityMetrics>();

  for (const food of flatData) {
    const key = `${food.day}-${food.kitchen}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        day: food.day,
        foods: []
      });
    }
    grouped.get(key)!.foods!.push(food);
  }

  return [...grouped.values()];
};

const useListProductivityMetricsQuery = ({ startDate, endDate, kitchen }: Props) => {
  const [productivityMetrics, setProductivityMetrics] = useState<ProductivityMetrics[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setProductivityMetrics([]);
    const intervals = splitDateRangeByDays(startDate, endDate, 4);
    const tasks = intervals.map(({ start, end }) => async () => {
      try {
        const { data } = await GraphQLClient.query<ListProductivityMetricsQuery, ListProductivityMetricsQueryVariables>({
          query: LIST_PRODUCTIVITY_METRICS_QUERY,
          variables: { startDate: start, endDate: end, kitchen },
          fetchPolicy: 'network-only'
        });
        return data?.listProductivityMetrics?.data ?? [];
      } catch (error) {
        toast.error(
          `Error fetching Productivity Metrics for ${start} - ${end}: ${error instanceof Error ? error.message : String(error)}`
        );
        return [];
      }
    });
    const results = await promisePoolForTasks(tasks, 5);
    const flatData = results.flat() as ProductivityMetricsFood[];
    const groupedData = groupProductivityMetricsByDayAndShift(flatData);
    setProductivityMetrics(groupedData);
    setLoading(false);
  }, [startDate, endDate, kitchen]);

  const clearData = () => {
    setProductivityMetrics([]);
    void GraphQLClient.clearStore();
  };

  useEffect(() => {
    void fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, kitchen]);

  return {
    productivityMetricsLoading: loading,
    productivityMetrics,
    clearProductivityMetricsData: clearData
  };
};

export default useListProductivityMetricsQuery;
